import type { Ticket } from "@kiraah-shared/common";
import { LvfTicketArticleNature } from "@kiraah-shared/common";
import type { TranslateService } from "@ngx-translate/core";
import jsPDF from "jspdf";
import { deliverPdfBlobToUser } from "./mobile-pdf-blob-save";

/** Détail lignes tableau section 2 — parité avec le web (`ngx-ticket-summary` + `TicketCreationSummaryComponent.downloadPDF`). */
export type MobileTicketDemandSummaryPdfDetailTable =
  | {
      readonly variant: "article";
      readonly rows: readonly { title: string; subtitle: string; quantity: string; unit: string }[];
      readonly primaryHeaderKey: string;
    }
  | {
      readonly variant: "equipment";
      readonly rows: readonly {
        identificationPrimary: string;
        registrationLine: string;
        designation: string;
      }[];
    }
  | {
      readonly variant: "vehicle_type";
      readonly rows: readonly { label: string; seats: string; energy: string }[];
    };

/** Données préparées côté UI (évite de coupler le générateur à l’orchestrateur). */
export interface MobileTicketDemandSummaryPdfModel {
  readonly label: string;
  readonly description: string;
  readonly category: string;
  readonly requester: string;
  readonly recipient: string;
  readonly creationDate: string;
  readonly authorName: string;
  readonly deliveryMode: string;
  readonly deliveryAddress: string;
  readonly detailTable: MobileTicketDemandSummaryPdfDetailTable;
  readonly ticket: Ticket;
}

/** #6f42c1 — même teinte que `TicketCreationSummaryComponent.downloadPDF`. */
const PRIMARY: [number, number, number] = [111, 66, 193];
const TEXT: [number, number, number] = [51, 51, 51];
const LIGHT_GRAY: [number, number, number] = [248, 249, 250];
const ROW_ZEBRA: [number, number, number] = [249, 249, 250];

/**
 * PDF récap demande e-achat — mise en page alignée sur le web (`ngx-ticket-summary`).
 * Navigateur : téléchargement classique. APK Capacitor : cache filesystem + feuille de partage système.
 */
export async function downloadMobileTicketDemandSummaryPdf(
  model: MobileTicketDemandSummaryPdfModel,
  translate: TranslateService,
): Promise<void> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentW = pageWidth - 2 * margin;
  let y = 30;

  const nature = model.ticket.TicketType?.LvfTicketArticleNature;
  let productsSectionTitle = translate.instant("mobile.eachat.wizard.summaryPdfSectionProducts");
  let emptyNatureKey = "mobile.eachat.wizard.summaryPdfNoneProducts";
  if (nature === LvfTicketArticleNature.VEHICLE) {
    productsSectionTitle = translate.instant("mobile.eachat.wizard.summaryPdfSectionVehicles");
    emptyNatureKey = "mobile.eachat.wizard.summaryPdfNoneVehicles";
  } else if (nature === LvfTicketArticleNature.VEHICLE_TYPE) {
    productsSectionTitle = translate.instant("mobile.eachat.wizard.summaryPdfSectionVehicleTypes");
    emptyNatureKey = "mobile.eachat.wizard.summaryPdfNoneVehicleTypes";
  } else if (nature === LvfTicketArticleNature.SERVICE) {
    productsSectionTitle = translate.instant("mobile.eachat.wizard.summaryPdfSectionServices");
    emptyNatureKey = "mobile.eachat.wizard.summaryPdfNoneServices";
  }

  const newPageIfNeeded = (minBottomSpace: number): void => {
    if (y > pageHeight - minBottomSpace) {
      doc.addPage();
      y = 30;
    }
  };

  // ----- Titre & séparateur (web)
  doc.setFontSize(24);
  doc.setTextColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
  doc.setFont("helvetica", "bold");
  doc.text(translate.instant("mobile.eachat.wizard.summaryPdfDocumentTitle"), margin, y);
  y += 20;
  doc.setDrawColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 15;

  // ----- 1. Informations générales
  doc.setFontSize(16);
  doc.setTextColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
  doc.setFont("helvetica", "bold");
  doc.text(`1. ${translate.instant("mobile.eachat.wizard.summaryPdfSectionGeneral")}`, margin, y);
  y += 10;

  const labelX = margin + 5;
  const valueX = margin + 40;
  /** Colonne valeur : s’arrête avant le bloc date / auteur (comme le web). */
  const valueWrap = Math.min(70, pageWidth - margin - 92 - 42);
  const rightLabelX = pageWidth - margin - 88;
  const rightValueX = pageWidth - margin - 52;
  const lineHeight = 5;

  type RowDef = { label: string; value: string };
  const leftRows: RowDef[] = [
    { label: translate.instant("mobile.eachat.field.label"), value: model.label },
    { label: translate.instant("mobile.eachat.field.comment"), value: model.description },
    { label: translate.instant("mobile.eachat.wizard.summaryPdfLabelCategory"), value: model.category },
    { label: translate.instant("mobile.eachat.field.requester"), value: model.requester },
    { label: translate.instant("mobile.eachat.wizard.summaryContactDelivery"), value: model.recipient },
  ];

  doc.setFontSize(10);
  const measureLeftHeight = (startY: number): number => {
    let yy = startY;
    for (const rowDef of leftRows) {
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(rowDef.value, valueWrap);
      yy += Math.max(8, lines.length * lineHeight) + 2;
    }
    return yy;
  };

  const blockTop = y - 5;
  const innerLeftY0 = y + 5;
  const simEndLeft = measureLeftHeight(innerLeftY0);
  /** Droite « date / auteur » : deux lignes espacées de 8 pts (comme le web). */
  const simRightEnd = innerLeftY0 + 8 + 8;
  const innerBottom = Math.max(simEndLeft, simRightEnd) + 5;
  const boxHeight = innerBottom - blockTop + 10;

  doc.setFillColor(LIGHT_GRAY[0], LIGHT_GRAY[1], LIGHT_GRAY[2]);
  doc.rect(margin, blockTop, contentW, boxHeight, "F");

  doc.setFontSize(10);
  doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
  let yL = innerLeftY0;
  for (const rowDef of leftRows) {
    doc.setFont("helvetica", "bold");
    doc.text(`${rowDef.label}:`, labelX, yL);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(rowDef.value, valueWrap);
    lines.forEach((line: string, i: number) => {
      doc.text(line, valueX, yL + i * lineHeight);
    });
    yL += Math.max(8, lines.length * lineHeight) + 2;
  }

  let yR = innerLeftY0;
  doc.setFont("helvetica", "bold");
  doc.text(`${translate.instant("mobile.eachat.wizard.summaryPdfLabelCreationDate")}:`, rightLabelX, yR);
  doc.setFont("helvetica", "normal");
  doc.text(model.creationDate, rightValueX, yR);
  yR += 8;
  doc.setFont("helvetica", "bold");
  doc.text(`${translate.instant("mobile.eachat.wizard.summaryPdfLabelAuthor")}:`, rightLabelX, yR);
  doc.setFont("helvetica", "normal");
  doc.text(model.authorName, rightValueX, yR);

  y = blockTop + boxHeight + 10;

  // ----- 2. Sélection — colonnes comme le web selon variant
  newPageIfNeeded(90);
  doc.setFontSize(16);
  doc.setTextColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
  doc.setFont("helvetica", "bold");
  doc.text(`2. ${productsSectionTitle}`, margin, y);
  y += 10;

  const colP = margin + 5;
  const colQ = margin + 86;
  const colU = margin + 128;
  const prodW = colQ - colP - 6;
  const colEquipmentDes = margin + 86;
  const colEquipmentTypeSeats = margin + 80;
  const colEquipmentTypeEnergy = margin + 118;
  const lh = 5;

  const rowCount =
    model.detailTable.variant === "article"
      ? model.detailTable.rows.length
      : model.detailTable.variant === "equipment"
        ? model.detailTable.rows.length
        : model.detailTable.rows.length;

  if (!rowCount) {
    doc.setFontSize(10);
    doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
    doc.setFont("helvetica", "italic");
    doc.text(translate.instant(emptyNatureKey), margin + 5, y);
    y += 12;
    doc.setFont("helvetica", "normal");
  } else {
    const headH = 8;
    doc.setFillColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
    doc.rect(margin, y - 5, contentW, headH, "F");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");

    if (model.detailTable.variant === "equipment") {
      doc.text(translate.instant("mobile.eachat.wizard.vehicleTableColVehicle"), colP, y);
      doc.text(translate.instant("mobile.eachat.wizard.vehicleTableColDesignation"), colEquipmentDes, y);
    } else if (model.detailTable.variant === "vehicle_type") {
      doc.text(translate.instant("mobile.eachat.wizard.vehicleTableColType"), colP, y);
      doc.text(translate.instant("mobile.eachat.wizard.vehicleTableColSeats"), colEquipmentTypeSeats, y);
      doc.text(translate.instant("mobile.eachat.wizard.vehicleTableColEnergy"), colEquipmentTypeEnergy, y);
    } else {
      doc.text(translate.instant(model.detailTable.primaryHeaderKey), colP, y);
      doc.text(translate.instant("mobile.eachat.field.quantity"), colQ, y);
      doc.text(translate.instant("mobile.eachat.wizard.unitLabel"), colU, y);
    }
    y += headH;

    doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    if (model.detailTable.variant === "article") {
      model.detailTable.rows.forEach((row, ix) => {
        newPageIfNeeded(42);
        const rowTop = y;
        const zebra = ix % 2 === 1;
        doc.setFontSize(10);

        const titleLines = doc.splitTextToSize((row.title || "—").trim(), prodW);
        let subLines: string[] = [];
        if (row.subtitle.trim()) {
          doc.setFontSize(8);
          subLines = doc.splitTextToSize(row.subtitle, prodW);
          doc.setFontSize(10);
        }
        const hTitle = titleLines.length * lh;
        const hSub = subLines.length ? subLines.length * 4 + 2 : 0;
        const rowH = Math.max(hTitle + hSub + 6, 14);

        if (zebra) {
          doc.setFillColor(ROW_ZEBRA[0], ROW_ZEBRA[1], ROW_ZEBRA[2]);
          doc.rect(margin, rowTop - lh, contentW, rowH + lh, "F");
        }

        doc.setFontSize(10);
        doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        let ty = rowTop;
        titleLines.forEach((ln: string) => {
          doc.text(ln, colP, ty);
          ty += lh;
        });
        if (subLines.length) {
          doc.setFontSize(8);
          doc.setTextColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
          subLines.forEach((ln: string) => {
            doc.text(ln, colP, ty);
            ty += 4;
          });
          doc.setFontSize(10);
          doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        }

        const qtyY = rowTop + Math.min(lh + 2, hTitle > 0 ? lh : 5);
        doc.text((row.quantity || "—").trim(), colQ, qtyY);
        doc.text((row.unit || "—").trim(), colU, qtyY);

        y = rowTop + rowH + 4;
      });
    } else if (model.detailTable.variant === "equipment") {
      const maxEquipmentW = 70;
      model.detailTable.rows.forEach((row, ix) => {
        newPageIfNeeded(48);
        const rowTop = y;
        const zebra = ix % 2 === 1;
        doc.setFontSize(10);

        const primary = (row.identificationPrimary || "—").trim();
        const reg = (row.registrationLine || "").trim();
        const equipmentLines = doc.splitTextToSize(primary, maxEquipmentW);
        let extraLines: string[] = [];
        let lineHeight = 5;
        if (reg && reg !== primary) {
          doc.setFontSize(8);
          extraLines = doc.splitTextToSize(reg, maxEquipmentW);
          doc.setFontSize(10);
        }
        const hTitle = equipmentLines.length * lineHeight;
        const hExtra = extraLines.length ? extraLines.length * 4 + 2 : 0;
        let totalLines = equipmentLines.length + extraLines.length;
        const rowH = Math.max(hTitle + hExtra + 6, 14);

        if (zebra) {
          doc.setFillColor(ROW_ZEBRA[0], ROW_ZEBRA[1], ROW_ZEBRA[2]);
          doc.rect(margin, rowTop - lh, contentW, rowH + lh, "F");
        }

        let currentY = rowTop;
        doc.setFontSize(10);
        doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        equipmentLines.forEach((ln: string) => {
          doc.text(ln, colP, currentY);
          currentY += lineHeight;
        });
        if (extraLines.length) {
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          extraLines.forEach((ln: string) => {
            doc.text(ln, colP, currentY);
            currentY += 4;
          });
          doc.setFontSize(10);
          doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        }

        const centerY =
          rowTop + Math.max(0, (Math.max(totalLines * lineHeight, rowH / 2) - lh) / 2);
        doc.text((row.designation || "—").trim(), colEquipmentDes, Math.min(centerY, rowTop + lh));

        y = rowTop + rowH + 4;
      });
    } else {
      const maxWtW = 70;
      model.detailTable.rows.forEach((row, ix) => {
        newPageIfNeeded(42);
        const rowTop = y;
        const zebra = ix % 2 === 1;
        const labelTxt = (row.label || "—").trim();
        let labelLines = doc.splitTextToSize(labelTxt, maxWtW);

        doc.setFontSize(10);
        const lineHeight = 5;
        let currentY = rowTop;
        const hLabel = Math.max(labelLines.length * lineHeight, 14);
        const rowH = hLabel + 4;

        if (zebra) {
          doc.setFillColor(ROW_ZEBRA[0], ROW_ZEBRA[1], ROW_ZEBRA[2]);
          doc.rect(margin, rowTop - lh, contentW, rowH + lh, "F");
        }

        doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        labelLines.forEach((ln: string) => {
          doc.text(ln, colP, currentY);
          currentY += lineHeight;
        });

        const centerY = rowTop + (labelLines.length * lineHeight) / 2 - 2;
        doc.text((row.seats || "—").trim(), colEquipmentTypeSeats, centerY);
        doc.text((row.energy || "—").trim(), colEquipmentTypeEnergy, centerY);

        y = currentY + 8;
      });
    }

    y += 6;
  }

  y += 8;

  // ----- 3. Adresse livraison
  newPageIfNeeded(55);
  doc.setFontSize(16);
  doc.setTextColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
  doc.setFont("helvetica", "bold");
  doc.text(`3. ${translate.instant("mobile.eachat.wizard.summaryPdfSectionDelivery")}`, margin, y);
  y += 10;

  if (!model.ticket.Delivery) {
    doc.setFontSize(10);
    doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
    doc.setFont("helvetica", "italic");
    doc.text(translate.instant("mobile.eachat.wizard.summaryPdfDeliveryMissing"), margin + 5, y);
  } else {
    const addrTxt = model.deliveryAddress.trim();
    doc.setFontSize(10);
    const addrLines = addrTxt ? doc.splitTextToSize(addrTxt, contentW - 50) : [];
    const delBoxTop = y - 5;
    /** Hauteur bloc : ligne mode + bloc adresse si présent (web ~30pt). */
    const delH = Math.max(30, 12 + (addrLines.length ? addrLines.length * lh + 8 : 0));

    doc.setFillColor(LIGHT_GRAY[0], LIGHT_GRAY[1], LIGHT_GRAY[2]);
    doc.rect(margin, delBoxTop, contentW, delH, "F");

    let yD = delBoxTop + 8;
    doc.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
    doc.setFont("helvetica", "normal");
    doc.text(`${translate.instant("mobile.eachat.wizard.summaryDeliveryMode")}:`, margin + 5, yD);
    doc.setFont("helvetica", "bold");
    doc.text(model.deliveryMode, margin + 40, yD);
    doc.setFont("helvetica", "normal");
    yD += 8;
    if (addrLines.length) {
      doc.text(`${translate.instant("mobile.eachat.wizard.summaryPdfLabelAddressLine")}:`, margin + 5, yD);
      addrLines.forEach((line: string, i: number) => {
        doc.text(line, margin + 40, yD + i * lh);
      });
    }
    y = delBoxTop + delH + 6;
  }

  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.setFont("helvetica", "normal");
  doc.text(
    translate.instant("mobile.eachat.wizard.summaryPdfGeneratedOn", {
      date: new Date().toLocaleDateString(translate.currentLang || "fr"),
    }),
    margin,
    pageHeight - 10,
  );

  const raw = (model.label || "Ticket").replace(/[/\\?%*:|"<>]/g, "-").slice(0, 60);
  const fileName = `Recapitulatif_Commande_${raw}_${new Date().toISOString().split("T")[0]}.pdf`;
  const blob = doc.output("blob");
  await deliverPdfBlobToUser(blob, fileName);
}
